/**
 * The names of the copies kept on this device, and their housekeeping (Phase 7, Lane O):
 * which copies exist, whose they are, and deleting them on a sign-out or a user switch.
 *
 * Split from ./store.ts so that the sign-in flow (prefs.tsx, part of every page) can do
 * this without loading yjs and y-indexeddb — those belong to the editor's chunk only.
 * Nothing here may import either of them.
 */
const PREFIX = 'studio-sheet:';
/** the names this browser created, for browsers without `indexedDB.databases()` */
const REGISTRY_KEY = 'studio.offline.stores';
/** whose copies are on this device */
const OWNER_KEY = 'studio.offline.owner';

export const storeName = (userId: number, sheetId: string) => `${PREFIX}${userId}:${sheetId}`;

export function registry(): string[] {
  try {
    const raw = window.localStorage.getItem(REGISTRY_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((n): n is string => typeof n === 'string' && n.startsWith(PREFIX)) : [];
  } catch {
    return [];
  }
}

export function writeRegistry(names: string[]): void {
  try {
    window.localStorage.setItem(REGISTRY_KEY, JSON.stringify([...new Set(names)]));
  } catch {
    /* the registry is a fallback for listing; IndexedDB itself may still list them */
  }
}

let probe: Promise<boolean> | null = null;

/**
 * Whether this browser lets the page keep data in IndexedDB. Asked once per page: a
 * private window or a blocked site refuses `open` with an error (or, in older browsers,
 * never answers — hence the time limit).
 */
export function storageAvailable(): Promise<boolean> {
  if (probe) return probe;
  probe = new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB) return done(false);
      const request = indexedDB.open('studio-offline-probe');
      request.onsuccess = () => {
        try {
          request.result.close();
        } catch {
          /* closed already */
        }
        done(true);
      };
      request.onerror = () => done(false);
      request.onblocked = () => done(false);
      setTimeout(() => done(false), 3000);
    } catch {
      done(false);
    }
  });
  return probe;
}

export function deleteDatabase(name: string): Promise<void> {
  writeRegistry(registry().filter((n) => n !== name));
  return new Promise<void>((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      // another tab still has it open; it is deleted once that one closes it
      request.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Delete one sheet's copy for one user (the sheet went to the bin, the role became viewer). */
export async function clearCopy(userId: number, sheetId: string): Promise<void> {
  if (!(await storageAvailable())) return;
  await deleteDatabase(storeName(userId, sheetId));
}

/** Every copy name on this device: what IndexedDB lists, and what this browser recorded. */
export async function listCopies(): Promise<string[]> {
  const names = new Set(registry());
  try {
    const dbs = typeof indexedDB !== 'undefined' && indexedDB.databases ? await indexedDB.databases() : [];
    for (const d of dbs) if (d.name?.startsWith(PREFIX)) names.add(d.name);
  } catch {
    /* listing is not supported everywhere; the registry covers this browser's own */
  }
  return [...names];
}

/**
 * Delete the copies on this device — all of them, or all but one user's. On a shared
 * computer the next person must not find, or merge, the previous one's sheets.
 */
export async function clearCopies(keepUserId?: number): Promise<number> {
  if (!(await storageAvailable())) return 0;
  const keep = keepUserId === undefined ? null : `${PREFIX}${keepUserId}:`;
  const doomed = (await listCopies()).filter((n) => !keep || !n.startsWith(keep));
  await Promise.all(doomed.map(deleteDatabase));
  return doomed.length;
}

/**
 * Keep the device's copies to the signed-in person: on a user switch the previous
 * person's are deleted, and on an explicit sign-out everybody's. A session that merely
 * expired keeps them — the same person signing in again gets their offline work back.
 */
export function noteSignedIn(userId: number): void {
  let owner: string | null = null;
  try {
    owner = window.localStorage.getItem(OWNER_KEY);
  } catch {
    owner = null;
  }
  if (owner !== String(userId)) void clearCopies(userId);
  try {
    window.localStorage.setItem(OWNER_KEY, String(userId));
  } catch {
    /* without a record, the next sign-in clears again, which is the safe side */
  }
}

export async function noteSignedOut(): Promise<void> {
  try {
    window.localStorage.removeItem(OWNER_KEY);
  } catch {
    /* nothing recorded */
  }
  await clearCopies();
}
